import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, AlertTriangle, Info, Zap, CheckCircle2, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const LEVEL_ICON = {
  attention: Info, alert: AlertTriangle, critical: AlertOctagon, incident: Zap,
};

const LEVEL_COLOR = {
  attention: "text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25",
  alert:     "text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/40",
  critical:  "text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/30",
  incident:  "text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/50",
};

/**
 * Compact widget: shows count per level + top 3 alerts. Links to /alerts.
 */
export default function AlertsWidget({ compact = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = () => {
      api.get("/alerts/summary")
        .then(r => { if (!cancelled) setData(r.data); })
        .catch(() => {});
    };
    fetch();
    const t = setInterval(fetch, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data) return null;
  const total = data.active_total || 0;
  const top = data.top_active || [];

  if (total === 0 && compact) {
    return (
      <div className="rounded-xl border border-[hsl(var(--success))]/30 bg-[hsl(var(--success-bg))]/60 p-3 flex items-center gap-2" data-testid="alerts-widget">
        <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" />
        <span className="text-sm text-[hsl(var(--success))] font-medium">Sem alertas activos</span>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="alerts-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Atenção necessária</div>
          <h3 className="font-display text-xl">
            {total} alerta{total === 1 ? "" : "s"} activo{total === 1 ? "" : "s"}
          </h3>
        </div>
        <Link to="/alerts" className="text-xs text-foreground hover:underline font-semibold inline-flex items-center gap-1"
              data-testid="alerts-widget-link">
          Ver todos <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {/* Level counts */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {["incident", "critical", "alert", "attention"].map(lvl => {
          const count = data.by_level?.[lvl] || 0;
          const Icon = LEVEL_ICON[lvl];
          return (
            <Link key={lvl} to={`/alerts?level=${lvl}`}
                  className={`rounded-lg border-2 p-2 text-center hover:shadow-sm ${LEVEL_COLOR[lvl]} ${count === 0 ? "opacity-40" : ""}`}
                  data-testid={`alerts-widget-${lvl}`}>
              <Icon className="w-3.5 h-3.5 mx-auto mb-1" strokeWidth={2} />
              <div className="font-display text-2xl leading-none">{count}</div>
            </Link>
          );
        })}
      </div>

      {/* Top active preview */}
      {top.length > 0 && (
        <div className="space-y-2">
          {top.map(a => {
            const Icon = LEVEL_ICON[a.level] || Info;
            return (
              <Link key={a.id} to={`/alerts?level=${a.level}`}
                    className={`block rounded-lg border p-2 text-xs hover:bg-muted/40 ${LEVEL_COLOR[a.level]}`}
                    data-testid={`alerts-widget-top-${a.id}`}>
                <div className="flex items-start gap-2">
                  <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-foreground truncate">{a.title}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{a.message}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
